import Protected from "@/pages/protected";
import { DataService } from "@/services/data";
import { MetadataService } from "@/services/metadata";
import { OptionsResponse } from "@/types/dynamic-detail";
import { registrationPrefillSchema } from "@/types/pre_configured_registration/prefill";
import { handle_error, handle_success } from "@/utils/wretch";
import { Navigate, Outlet, RouteObject, defer, json, useLoaderData } from "react-router-dom";

const PreConfiguredRegistrationRoute: RouteObject = {
    path: "ventes",
    element: (
        <Protected excludedProfiles={["iperia_operator"]}>
            <Outlet />
        </Protected>
    ),
    children: [
        {
            index: true,
            async lazy() {
                const { ObjectListLayout2 } = await import("@/components/layout-2/object-list-2");
                const AccessDenied = (await import("@/pages/access-denied")).default;
                return {
                    Component: () => {
                        const { metadata } = useLoaderData() as { metadata: OptionsResponse };
                        if (!metadata) return <div>Error loading metadata</div>;
                        if (!metadata.is_access_allowed) return <AccessDenied />;
                        return (
                            <ObjectListLayout2<{ id: number }>
                                metadata={metadata}
                                endpointUrl={`/crm/objects/${metadata.object_url}/`}
                                primary_column={metadata.list_config?.primary_column ?? "prospect_name"}
                                page_title={metadata?.model_name_plural ?? "Inscriptions"}
                                onRowNavigateTo={instance => {
                                    if (metadata.detail_base_url) {
                                        return `${metadata.detail_base_url}${instance.id}`;
                                    }
                                    return `/inscriptions/ventes/${instance.id}`;
                                }}
                                allowBatchSelection={true}
                                itemToId={instance => instance.id.toString()}
                                // The forms the server declares for this page (`FormConfig.list_pages`): none today,
                                // sales staff create through « Nouvelle inscription » (`/inscriptions/ventes/nouveau`);
                                // the creation forms are offered by `/registrations` (the main list page).
                                listPage="sales"
                            />
                        );
                    }
                };
            },
            loader: async () => {
                const metadata = await MetadataService.get("/crm/objects/registrations/");
                return { metadata };
            }
        },
        {
            path: "nouveau",
            action: async ({ request }) => {
                const data = await request.json();
                const url = new URL(request.url);
                const action = url.searchParams.get("action");
                // Bouton "Nouvelle inscription" (cf. RegistrationGroup) : forwardé depuis l'URL par
                // les formulaires de création (leur `fetcher.submit` inclut `location.search` dans
                // l'action) plutôt que porté par chaque formulaire individuellement.
                const sourceRegistrationId = url.searchParams.get("source_registration_id");
                if (sourceRegistrationId && !action) {
                    data.source_registration_id = sourceRegistrationId;
                }
                try {
                    const content = await (action ? DataService.client.url(`/registration/sales/${action}/`).post(data).json() : DataService.client.url(`/registration/sales/`).post(data).json());
                    return handle_success(content);
                } catch (error: any) {
                    return handle_error(error);
                }
            },
            children: [
                {
                    index: true,
                    async lazy() {
                        let { CreatePage } = await import("@/pages/registration/pre_configured_registration/create");
                        return { Component: CreatePage };
                    }
                },
                {
                    // Reprise des informations d'un dossier précédent du même prospect : route de
                    // données uniquement, consommée par `useSourceRegistrationPrefill`.
                    path: "reprise",
                    loader: async ({ request }) => {
                        const url = new URL(request.url);
                        const sourceRegistrationId = url.searchParams.get("source_registration_id");
                        if (!sourceRegistrationId) {
                            return handle_success(null);
                        }
                        const fundingOrganismCode = url.searchParams.get("funding_organism_code");
                        const query = fundingOrganismCode ? `?funding_organism_code=${encodeURIComponent(fundingOrganismCode)}` : "";
                        try {
                            const content = await DataService.client
                                .url(`/registration/sales/${encodeURIComponent(sourceRegistrationId)}/prefill/${query}`)
                                .get()
                                .json();
                            return handle_success(registrationPrefillSchema.parse(content));
                        } catch (error: any) {
                            return handle_error(error);
                        }
                    }
                },
                {
                    path: "cpf",
                    async lazy() {
                        let { CreateCPFPage } = await import("@/pages/registration/pre_configured_registration/create/cpf");
                        return { Component: CreateCPFPage };
                    }
                },
                {
                    path: "dpc",
                    async lazy() {
                        let { CreateDPCPage } = await import("@/pages/registration/pre_configured_registration/create/dpc");
                        return { Component: CreateDPCPage };
                    }
                },
                {
                    path: "iperia",
                    async lazy() {
                        let { CreateIperiaPage } = await import("@/pages/registration/pre_configured_registration/create/iperia");
                        return { Component: CreateIperiaPage };
                    }
                },
                {
                    path: "fifpl",
                    async lazy() {
                        let { CreateFIFPLPage } = await import("@/pages/registration/pre_configured_registration/create/fifpl");
                        return { Component: CreateFIFPLPage };
                    }
                },
                {
                    path: "fafpm",
                    async lazy() {
                        let { CreateFAFPMPage } = await import("@/pages/registration/pre_configured_registration/create/fafpm");
                        return { Component: CreateFAFPMPage };
                    }
                },
                {
                    path: "kairos",
                    async lazy() {
                        let { CreateKairosPage } = await import("@/pages/registration/pre_configured_registration/create/kairos");
                        return { Component: CreateKairosPage };
                    }
                },
                {
                    path: "gocardless",
                    async lazy() {
                        let { CreateGoCardlessPage } = await import("@/pages/registration/pre_configured_registration/create/gocardless");
                        return { Component: CreateGoCardlessPage };
                    }
                },
                {
                    path: "opco",
                    async lazy() {
                        let { CreateOPCOPage } = await import("@/pages/registration/pre_configured_registration/create/opco");
                        return { Component: CreateOPCOPage };
                    }
                },
                {
                    path: "formation-offerte",
                    async lazy() {
                        let { CreateGiveawayPage } = await import("@/pages/registration/pre_configured_registration/create/giveaway");
                        return { Component: CreateGiveawayPage };
                    }
                },
                {
                    path: "autres",
                    async lazy() {
                        let { CreateOtherPage } = await import("@/pages/registration/pre_configured_registration/create/other");
                        return { Component: CreateOtherPage };
                    }
                }
            ]
        },
        {
            path: "api",
            loader: async ({ request }) => {
                const url = new URL(request.url);
                if (!url.searchParams.has("registration_type")) {
                    url.searchParams.set("registration_type", "PreConfiguredRegistration");
                }
                if (!url.searchParams.has("page_size")) {
                    url.searchParams.set("page_size", "25");
                }
                try {
                    const registrations = await DataService.client
                        .url(`/registration/sales/${url.searchParams ? "?" + url.searchParams.toString() : ""}`)
                        .get()
                        .json();
                    return handle_success(registrations);
                } catch (error) {
                    return handle_error(error);
                }
            }
        },
        {
            id: "preconfiguredregistration-pending",
            path: "en-attente",
            async lazy() {
                let { ListPendingPage } = await import("@/pages/registration/pre_configured_registration/list-pending");
                return { Component: ListPendingPage };
            },
            loader: async ({ request }) => {
                const url = new URL(request.url);
                if (!url.searchParams.has("page_size")) {
                    url.searchParams.set("page_size", "25");
                }
                const registrations = DataService.client
                    .url(`/registration/admin/funding_organism_registrations/${url.searchParams ? "?" + url.searchParams.toString() : ""}`)
                    .get()
                    .json();
                return defer({ registrations });
            },
            children: [
                {
                    path: ":registrationId",
                    action: async ({ params, request }) => {
                        const registrationId = params.registrationId;
                        const data = await request.json();
                        const url = new URL(request.url);
                        const action = url.searchParams.get("action");
                        try {
                            const content = await (action
                                ? DataService.client.url(`/registration/admin/funding_organism_registrations/${registrationId}/${action}/`).post(data).json()
                                : DataService.client.url(`/registration/admin/funding_organism_registrations/${registrationId}/`).patch(data).json());
                            return json({ success: true, content: content });
                        } catch (error: any) {
                            return handle_error(error);
                        }
                    }
                }
            ]
        },
        {
            id: "preconfiguredregistration-detail",
            path: ":registrationId",
            async lazy() {
                let { DetailLayout } = await import("@/pages/registration/pre_configured_registration/detail-layout");
                return { Component: DetailLayout };
            },

            loader: async ({ params }) => {
                const registrationId = params.registrationId;
                const registration = await DataService.client.url(`/registration/sales/${registrationId}/`).get().json();
                const invoices = await DataService.client.url(`/registration/sales/${registrationId}/invoices/`).get().json();
                const professions = DataService.client.url(`/content/professions/?page_size=50`).get().json();
                return defer({ registration, invoices, professions });
            },
            action: async ({ params, request }) => {
                const registrationId = params.registrationId;
                const data = await request.json();
                const url = new URL(request.url);
                const action = url.searchParams.get("action");
                try {
                    const content = await (action
                        ? DataService.client.url(`/registration/sales/${registrationId}/${action}/`).post(data).json()
                        : DataService.client.url(`/registration/sales/${registrationId}/`).patch(data).json());
                    return handle_success(content);
                } catch (error: any) {
                    return handle_error(error);
                }
            },
            children: [
                {
                    index: true,
                    element: <Navigate to="formations" replace />
                },
                {
                    path: "formations",
                    async lazy() {
                        let { ProductsPage } = await import("@/pages/registration/pre_configured_registration/detail-products");
                        return { Component: ProductsPage };
                    }
                },
                {
                    path: "fifpl-documents",
                    async lazy() {
                        let { FifplDocumentsPage } = await import("@/pages/registration/pre_configured_registration/detail-fifpl-documents");
                        return { Component: FifplDocumentsPage };
                    },
                    loader: async ({ params }) => {
                        const registrationId = params.registrationId;
                        const documents = await DataService.client.url(`/registration/sales/${registrationId}/documents/`).get().json();
                        return { documents };
                    },
                    action: async ({ params, request }) => {
                        try {
                            const content = await DataService.client
                                .url(`/registration/sales/${params.registrationId}/fifpl/documents/review/`)
                                .post(await request.json())
                                .json();
                            return handle_success(content);
                        } catch (error: unknown) {
                            return handle_error(error);
                        }
                    }
                },
                {
                    path: "bulletin",
                    async lazy() {
                        let { DocumentPage } = await import("@/pages/registration/pre_configured_registration/detail-bulletin");
                        return { Component: DocumentPage };
                    }
                },
                {
                    path: "journal",
                    async lazy() {
                        let { JournalPage } = await import("@/pages/registration/pre_configured_registration/detail-journal");
                        return { Component: JournalPage };
                    }
                },
                {
                    path: "iperia-documents",
                    async lazy() {
                        let { IperiaDocumentsPage } = await import("@/pages/registration/pre_configured_registration/detail-iperia-documents");
                        return { Component: IperiaDocumentsPage };
                    },
                    loader: async ({ params }) => {
                        const registrationId = params.registrationId;
                        const documents = await DataService.client.url(`/registration/sales/${registrationId}/documents/`).get().json();
                        return { documents };
                    },
                    action: async ({ params, request }) => {
                        try {
                            const content = await DataService.client
                                .url(`/registration/sales/${params.registrationId}/iperia/documents/review/`)
                                .post(await request.json())
                                .json();
                            return handle_success(content);
                        } catch (error: unknown) {
                            return handle_error(error);
                        }
                    }
                },
                {
                    path: "financement",
                    async lazy() {
                        let { FundingPage } = await import("@/pages/registration/pre_configured_registration/detail-funding");
                        return { Component: FundingPage };
                    }
                },
                {
                    path: "positionnement",
                    element: <Navigate to="../formations" replace />
                },
                {
                    path: "documents",
                    async lazy() {
                        let { DocumentsPage } = await import("@/pages/registration/pre_configured_registration/detail-documents");
                        return { Component: DocumentsPage };
                    },
                    loader: async ({ params }) => {
                        const registrationId = params.registrationId;
                        const documents = await DataService.client.url(`/registration/sales/${registrationId}/documents/`).get().json();
                        return { documents };
                    }
                },
                {
                    path: "autres-inscriptions",
                    async lazy() {
                        let { RelatedRegistrationsPage } = await import("@/pages/registration/pre_configured_registration/detail-related-registrations");
                        return { Component: RelatedRegistrationsPage };
                    }
                },
                {
                    path: "cases",
                    async lazy() {
                        let { CasesPage } = await import("@/pages/registration/pre_configured_registration/detail-cases");
                        return { Component: CasesPage };
                    },
                    loader: async ({ params }) => {
                        const registrationId = params.registrationId;
                        const cases = await DataService.client.url(`/registration/sales/${registrationId}/cases/`).get().json();
                        return { cases };
                    }
                },
                {
                    path: "custom_process/:customProcessId",
                    action: async ({ params, request }) => {
                        const customProcessId = params.customProcessId;
                        const data = await request.json();
                        const url = new URL(request.url);
                        const action = url.searchParams.get("action");
                        try {
                            const content = await (action
                                ? DataService.client.url(`/registration/pre_registration_custom_processes/${customProcessId}/${action}/`).post(data).json()
                                : DataService.client.url(`/registration/pre_registration_custom_processes/${customProcessId}/`).patch(data).json());
                            return handle_success(content);
                        } catch (error: any) {
                            return handle_error(error);
                        }
                    }
                }
            ]
        }
    ]
};
export default PreConfiguredRegistrationRoute;
